import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';

import {
  defaultThemeName,
  getThemeIdFromRoute,
  getThemeIdFromStateAndRoute,
  setFavicons
} from '@/config/themes.js';
import { setupRouter } from '@/router/index.js';
import { getVuetifyInstance } from '@/plugins/vuetify.js';
import { createHead } from '@unhead/vue/client';
import createRttI18n, { getStartLocale } from '@/plugins/rttI18nPlugin.js';
import { inputToTenant } from '@/config/countriesAndLocales.js';
import createRttMoment from '@/plugins/rttMomentPlugin.js';
import localePlugin from '@/plugins/localePlugin.js';
import { validateLocale } from '@/services/localeValidation.js';
import oberserverPlugin from '@/plugins/observerPlugin.js';
import defaultConfig from '@/config/defaultConfig';
import { getUtmSource } from '@/router/queryParam.js';
import { useThemeStore } from '@/stores/theme.js';
import { useGdprStore } from '@/stores/gdpr.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useAppStore } from '@/stores/app.js';
import { sendAnalytics } from '@/services/analytics.js';
import { FocusTrap } from 'focus-trap-vue';

export async function init(urlPath, app, containerId, isWidget) {
  const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;
  const shouldInitSentry =
    Boolean(sentryDsn) &&
    import.meta.env.VITE_APP_VERSION !== 'local' &&
    import.meta.env.MODE !== 'test';

  if (shouldInitSentry) {
    try {
      Sentry.init({
        app,
        dsn: sentryDsn,
        environment: import.meta.env.VITE_APP_ENV,
        release: import.meta.env.VITE_APP_VERSION,
        sendDefaultPii: false,
        initialScope: {
          tags: {
            isWidget: String(isWidget)
          }
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Sentry initialization failed', error);
    }
  }

  app.config.productionTip = false;
  app.use(createHead());

  const pinia = createPinia();
  app.use(pinia);

  const router = setupRouter(isWidget);
  const route = router.resolve(urlPath);
  app.use(router);
  if (isWidget) await router.replace(route);
  const { params } = route;

  const tenantId = inputToTenant(params.countryOrTenantId);
  const startLocale = getStartLocale(params.locale);
  try {
    validateLocale(startLocale);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);
  }
  const themeIdFromRoute = getThemeIdFromRoute(route);
  const rttI18n = createRttI18n({
    locale: startLocale,
    tenantId,
    isWidget,
    themeId: themeIdFromRoute
  });
  app.use(rttI18n);
  const rttMoment = createRttMoment({ locale: startLocale, tenantId });
  app.use(rttMoment);
  app.use(localePlugin);
  app.use(oberserverPlugin);

  const appStore = useAppStore();
  appStore.setContainerId(containerId);

  const themeStore = useThemeStore();
  const trackingDataStore = useTrackingDataStore();
  const gdprStore = useGdprStore();

  const setTheme = themeId =>
    themeStore.setThemeId(themeId || defaultThemeName());

  if (params.trackingId) {
    const payload = {
      ...params,
      $route: route,
      tenantId,
      utmSource: getUtmSource(route)
    };

    const hasGdprConsent =
      defaultConfig.widgetConfiguration.gdprApproved ||
      gdprStore.hasGdprConsent(tenantId);

    if (hasGdprConsent && params.postcode) {
      await trackingDataStore.loadAdditionalTrackingData(payload);
      sendAnalytics(trackingDataStore.data, 'global', 'page_load', {
        isV2: Boolean(
          route.path.includes('/preview') ||
            trackingDataStore.data.enabledNewDesign
        )
      });
    } else {
      await trackingDataStore.loadTrackingDataById(payload);
      sendAnalytics(trackingDataStore.data, 'global', 'page_load', {
        isV2: Boolean(
          route.path.includes('/preview') ||
            trackingDataStore.data.enabledNewDesign
        )
      });
    }
    if (!hasGdprConsent && params.postcode) {
      if (
        route.path.includes('/preview') ||
        trackingDataStore.data.enabledNewDesign
      ) {
        await router.replace({
          name: 'previewPostCodePage',
          params: {
            countryOrTenantId: tenantId,
            locale: startLocale,
            trackingId: params.trackingId
          }
        });
      } else {
        await router.replace({
          name: 'realTimeTracking',
          params: {
            trackingId: params.trackingId,
            countryOrTenantId: tenantId,
            locale: startLocale
          },
          query: { utm_source: getUtmSource(route) }
        });
      }
    } else if (
      trackingDataStore.requestState === 'SUCCESS' &&
      !route.path.includes('/preview') &&
      trackingDataStore.data.enabledNewDesign &&
      !isWidget
    ) {
      await router.replace('/preview' + route.path);
    }
  }

  let themeId;

  if (!params.trackingId || themeIdFromRoute) {
    themeId = themeIdFromRoute;
  } else {
    themeId = getThemeIdFromStateAndRoute(trackingDataStore.data, route);
  }

  const themeName = themeId || defaultThemeName();
  const vuetifyInstance = getVuetifyInstance(themeName);

  setFavicons(themeName);

  app.use(vuetifyInstance);

  setTheme(themeId);
  app.component('FocusTrap', FocusTrap).mount(`#${containerId}`);
}
